/**
 * The figures behind a reel's stat line — `{n} outcomes · {n} enabled · {n}
 * fixtures · {n} markets · ({n} suspended)` (CAE-1574).
 *
 * Here rather than beside the Reels section because two screens draw the same
 * five numbers from the same five words: the Contest Overview's section
 * (`.vw-reel-stats`) and Edit › Reels' breakdown strip (`.rl-breakdown`,
 * CAE-1552), whose specs carry that line character for character. The same
 * argument `readiness.ts` makes for living here — one derivation, so the two
 * cannot disagree about a reel an operator sees on both within one click.
 */
import type { ContestDetailReelOption, ReelKind } from '../api/contests';
import { reelOptionMarketLabel } from './reel-option-market-label';

export interface ReelStats {
  options: number;
  enabled: number;
  /**
   * How many *distinct* fixtures the reel's options draw from — not how many
   * options there are. A reel of three outcomes on one match is one fixture,
   * and the line says so.
   */
  fixtures: number;
  /** Distinct markets, counted the same way. */
  markets: number;
  /** Options the book has taken off. Drawn only when there are any. */
  suspended: number;
  /**
   * Enabled options with no artwork attached — the one number Edit › Reels
   * raises an alarm about (CAE-1552).
   *
   * Here rather than in the screen because three things have to agree about it
   * within one glance: the reel rail's state dot, the warn banner over the
   * outcome list, and the Setup Rail's amber on the Reels step. The rail reads
   * this directly and `readiness.ts` reads it too, so the screen cannot tell an
   * operator a reel is fine while the step above it says otherwise — which is
   * CAE-1552's second acceptance criterion, kept as structure rather than as a
   * test that would only notice the day the two were already wrong.
   *
   * **Enabled only.** A disabled option with no artwork is not a problem to
   * report: nothing is going to put it in front of a player.
   */
  missingAssets: number;
}

/**
 * Markets are counted by the **label the rows carry**, not by the raw
 * `externalMarketId`.
 *
 * The head and the table under it have to describe the same reel, and
 * `reelOptionMarketLabel` prefers `marketName` over the id before falling back
 * to `Not set`. Keying on the id alone broke that both ways: two options the
 * feed gave no id but two different market names read as one market over two
 * visibly different rows, and two options sharing a name under different ids
 * read as two markets over rows an operator cannot tell apart. Options the feed
 * named no market for at all still count as one unnamed market between them,
 * because `Not set` is the single label they all render as.
 */
const distinct = (values: readonly (string | null)[]): number => new Set(values).size;

/**
 * The one question "is this option short of artwork?" is asked by.
 *
 * `missingAssets` counts these; `readiness.ts` *names* them for the publish
 * checklist (CAE-1595). Those are a tally and a list of the same thing, and
 * two copies of `enabled && !assetId` are two chances for the checklist to
 * name an option the banner above it does not count — the exact drift the
 * `missingAssets` note argues against. One predicate, two readings.
 */
/**
 * The image an option's club lends it when nothing is attached by hand (GL-12;
 * CAE-1610): the Generic Jersey on a Player Market Reel, the Team Logo on a
 * Game Market Reel, and either when the Reel's kind is not to hand. Null
 * without a club, or while the club has no such image.
 */
export const clubImageAssetId = (
  option: ContestDetailReelOption,
  kind?: ReelKind | null,
): string | null => {
  if (kind === 'player') return option.clubJerseyAssetId;
  if (kind === 'game') return option.clubLogoAssetId;
  return option.clubJerseyAssetId ?? option.clubLogoAssetId;
};

export const needsArtwork = (option: ContestDetailReelOption, kind?: ReelKind | null): boolean =>
  option.enabled && !option.assetId && !clubImageAssetId(option, kind);

export const reelStats = (
  options: readonly ContestDetailReelOption[],
  kind?: ReelKind | null,
): ReelStats => ({
  options: options.length,
  enabled: options.filter((option) => option.enabled).length,
  fixtures: distinct(options.map((option) => option.contestFixtureId)),
  markets: distinct(options.map((option) => reelOptionMarketLabel(option))),
  suspended: options.filter((option) => option.suspended === true).length,
  missingAssets: options.filter((option) => needsArtwork(option, kind)).length,
});
